import { useTranslation } from 'components/translation/TranslationProvider';
import { FormDate } from 'components/form/FormDate';
import { FormField } from 'components/form/FormField';
import { FormInput } from 'components/form/FormInput';
import { FormPhone } from 'components/form/FormPhone';
import { FormRadioList } from 'components/form/FormRadioList';
import { FormSelect } from 'components/form/FormSelect';
import { React } from 'libs/react';
import { FormMultiSelect } from 'components/form/FormMultiSelect';
import { isArray } from 'lodash';
import { TranslatableFormInput } from 'components/form/TranslatableFormInput';
import { useAccount } from 'components/account/AccountProvider';
import { useForm } from 'components/form/Form';
import { parseTranslatableFieldEnvelope } from 'helpers/Field';
import { isRichTextEmpty } from 'helpers/richText';
import { FormRichTextInput } from 'components/form/LazyFormRichTextInput';
import { toMoney } from 'components/Money';
import i18n from 'components/translation/i18n';
import { FormMediaPicker } from 'components/media/form/FormMediaPicker';
import { FormToggle } from 'components/form/FormToggle';
import { FormCheck } from 'components/form/FormCheck';
import './CustomFormField.scss';
import { TranslatableRichTextInput } from 'components/form/TranslatableRichTextInput';
const EMPTY_MULTISELECT = [];
function parseCustomFieldDisplayValue(fieldValue, populate) {
    var _a, _b;
    if (!populate) {
        return '';
    }
    if (fieldValue == null || fieldValue === '') {
        return '';
    }
    if (typeof fieldValue === 'object') {
        return (_a = fieldValue.value) !== null && _a !== void 0 ? _a : '';
    }
    if (typeof fieldValue !== 'string') {
        return String(fieldValue);
    }
    try {
        const parsed = JSON.parse(fieldValue);
        if (typeof parsed === 'object' && parsed !== null && 'value' in parsed) {
            return (_b = parsed.value) !== null && _b !== void 0 ? _b : '';
        }
    }
    catch (_c) {
        return fieldValue;
    }
    return fieldValue;
}
function normalizedMultiSelectArray(value) {
    if (value == null || value === '') {
        return EMPTY_MULTISELECT;
    }
    if (isArray(value) && value.length > 0) {
        return value;
    }
    if (typeof value === 'object') {
        const keys = Object.keys(value).filter((key) => value[key] === true);
        return keys.length > 0 ? keys : EMPTY_MULTISELECT;
    }
    return EMPTY_MULTISELECT;
}
function parseCustomFieldStoredValue(fieldValue, populate) {
    var _a, _b;
    if (!populate)
        return '';
    if (fieldValue == null || fieldValue === '')
        return '';
    if (typeof fieldValue !== 'string')
        return fieldValue;
    try {
        return (_b = (_a = JSON.parse(fieldValue)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
    }
    catch (_c) {
        return '';
    }
}
/**
 * A missing default is stored as null. `null != ''` is true, so that check
 * must not be used to decide whether a default was configured.
 */
function hasConfiguredDefaultValue(value) {
    if (value == null || value === '') {
        return false;
    }
    const display = parseCustomFieldDisplayValue(value, true);
    if (display == null || display === '') {
        return false;
    }
    if (typeof display === 'string') {
        return !isRichTextEmpty(display);
    }
    if (Array.isArray(display)) {
        return display.length > 0;
    }
    if (typeof display === 'object') {
        return Object.keys(display).length > 0;
    }
    return true;
}
/**
 * @component CustomFormField
 * @since 1.0.0
 * @hidden
 */
export function CustomFormField(props) {
    const { field, functionValue, populate, noDefaultValue, noLabel, size, translable, onBlur, onChange, fromPublic, includeInactiveOptions } = Object.assign(Object.assign({}, props), { populate: true });
    const account = useAccount();
    const form = useForm();
    let { language } = useTranslation();
    const i18nLang = i18n.language.substring(0, 2);
    if (language == null || language == '') {
        language = i18nLang;
    }
    /**
     * @function formatFieldName
     * @since 1.0.0
     * @hidden
     */
    function formatFieldName(id) {
        return 'fields.' + id;
    }
    /**
     * @function formatFieldValue
     * @since 1.0.0
     * @hidden
     */
    function formatFieldValue(value) {
        return parseCustomFieldDisplayValue(value, populate);
    }
    /**
     * @function formatFieldTranslations
     * @since 1.0.0
     * @hidden
     */
    function formatFieldTranslations(field) {
        var _a;
        const translationsData = [];
        const rawSource = field.value != null && field.value !== ''
            ? field.value
            : field.defaultValue;
        const fieldValues = parseTranslatableFieldEnvelope(rawSource);
        const translations = (_a = fieldValues.translations) !== null && _a !== void 0 ? _a : {};
        account.languages.forEach((language, index) => {
            var _a, _b;
            translationsData[index] = {
                language: language,
                ['fields.' + field.id]: (_b = (_a = translations[language.code]) !== null && _a !== void 0 ? _a : fieldValues.value) !== null && _b !== void 0 ? _b : ''
            };
        });
        return populate ? (translationsData !== null && translationsData !== void 0 ? translationsData : '') : '';
    }
    /**
     * @function formatFieldOptions
     * @since 1.0.0
     * @hidden
     */
    function formatFieldOptions(options, selected) {
        var _a;
        let choices = (_a = JSON.parse(options)) === null || _a === void 0 ? void 0 : _a.choices;
        if (choices) {
            const selectedValues = (isArray(selected) ? selected : [selected])
                .filter((value) => value != null && value !== '')
                .map(String);
            // Inactive options are no longer offered, unless already saved here
            if (!includeInactiveOptions) {
                choices = choices.filter((choice) => choice.active !== false ||
                    selectedValues.includes(String(choice.value)));
            }
            choices = choices.map((choice) => {
                let label = choice.label;
                const value = choice.value;
                const order = choice.order;
                if (label instanceof Array) {
                    let match = null;
                    if (match == null)
                        match = label.find((entry) => entry.language == language);
                    if (match == null)
                        match = label[0];
                    if (match) {
                        label = match.label;
                    }
                    else {
                        label = t('Unlabeled');
                    }
                }
                if (choice.active === false && !includeInactiveOptions) {
                    label = `${label} (${t('Inactive')})`;
                }
                return {
                    label,
                    value,
                    order
                };
            });
            // Sort choices by order ascending or by label
            choices.sort((a, b) => {
                if (a.order != null && b.order != null) {
                    return a.order - b.order;
                }
                else {
                    return a.label.localeCompare(b.label);
                }
            });
            return choices;
        }
        return [];
    }
    const multiSelectStableValue = React.useMemo(() => {
        if (field.type !== 'MULTISELECT')
            return null;
        const useDefaultFallback = field.value == null &&
            hasConfiguredDefaultValue(field.defaultValue) &&
            !noDefaultValue;
        const envelopeSource = useDefaultFallback ? field.defaultValue : field.value;
        const rawForMulti = parseCustomFieldStoredValue(envelopeSource, populate);
        return normalizedMultiSelectArray(rawForMulti);
        // FormMultiSelect: useValue + useWatch re-fire onChange when `value` prop
        // reference changes. Fresh `[]` / parsed arrays each parent render → setDeal loop.
    }, [
        field.type,
        field.id,
        field.value,
        field.defaultValue,
        noDefaultValue,
        populate
    ]);
    /**
     * @function renderField
     * @since 1.0.0
     * @hidden
     */
    function renderField() {
        var _a, _b;
        const name = formatFieldName((_a = props.fieldId) !== null && _a !== void 0 ? _a : field.id);
        const defaultValue = field.defaultValue;
        const defaultValueLocked = field.defaultValueLocked;
        let tooltip = field.infos;
        let value = field.value;
        if (field.value == null &&
            hasConfiguredDefaultValue(defaultValue) &&
            !noDefaultValue) {
            value = formatFieldValue(defaultValue);
            tooltip = t('This value was set by default');
            if (defaultValueLocked) {
                tooltip = t('This value was set by default and cannot be changed');
            }
        }
        else {
            value = formatFieldValue(field.value);
        }
        const translations = formatFieldTranslations(field);
        const baseTitle = noLabel
            ? ''
            : (_b = field.translations.find((t) => t.language.code === language)) === null || _b === void 0 ? void 0 : _b.title;
        // Ajouter une mention si le champ est inactif mais affiché (car il a une valeur)
        const isInactiveWithValue = field.active === false && field.value && field.value !== 'null' && field.value !== '{}';
        const title = isInactiveWithValue ? `${baseTitle} (${t('Inactive Field')})` : baseTitle;
        const infos = tooltip;
        switch (field.type) {
            case 'TEXTBOX':
                if (!translable) {
                    return (React.createElement(FormInput, { name: name, label: title, value: value, tooltip: infos, onChange: (value) => {
                            onChange && onChange({ [field.id]: value });
                        }, onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
                }
                else {
                    return (React.createElement(TranslatableFormInput, { name: name, path: "translations", label: title, values: translations, languages: account.languages, onChange: (language, value) => onChange && onChange({ [field.id]: '' }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
                }
            case 'TEXTAREA':
                if (!translable) {
                    return (React.createElement(FormRichTextInput, { name: name, label: title, value: value, tooltip: infos, onChange: (newValue) => {
                            onChange && onChange({ [field.id]: newValue });
                        }, onBlur: () => {
                            const current = form.getValue(name);
                            onBlur && onBlur({ [field.id]: current });
                        }, disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
                }
                else {
                    return (React.createElement(TranslatableRichTextInput, { name: name, path: "translations", label: title, values: translations, languages: account.languages, placeholder: t('Enter Description'), onChange: (language, value) => onChange && onChange({ [field.id]: '' }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false }));
                }
            case 'URL':
                return (React.createElement(FormInput, { type: "url", name: name, label: title, value: value, tooltip: infos, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'NUMBER':
                return (React.createElement(FormInput, { type: "number", name: name, label: title, value: value, tooltip: infos, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'MONEY':
                return (React.createElement(FormInput, { type: "number", name: name, label: title, value: value, tooltip: infos, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size, suffix: '$' }));
            case 'PHONE':
                return (React.createElement(FormPhone, { name: name, label: title, value: value, tooltip: infos, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'EMAIL':
                return (React.createElement(FormInput, { type: "email", name: name, label: title, value: value, tooltip: infos, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'YESNO':
                return (React.createElement(FormToggle, { name: name, label: title, infos: infos, value: value, options: [
                        { label: t('No'), value: '0' },
                        { label: t('Yes'), value: '1' }
                    ], onChange: (newValue) => {
                        if (onChange && newValue !== value) {
                            onChange({ [field.id]: newValue });
                        }
                    }, disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'CHECKBOX':
                return (React.createElement(FormCheck, { name: name, label: title, infos: infos, checked: value, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'SELECT_RADIO':
                return (React.createElement(FormRadioList, { name: name, label: title, infos: infos, value: value, options: formatFieldOptions(field.options, value), onChange: (value) => onChange && onChange({ [field.id]: value }), 
                    //onBlur={() => onBlur && onBlur({ [field.id]: value })}
                    disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false }));
            case 'SELECT_DROPDOWN':
                return (React.createElement(FormSelect, { name: name, label: title, value: value, tooltip: infos, options: formatFieldOptions(field.options, value), onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size, skipSort: true }));
            case 'MULTISELECT':
                return (React.createElement(FormMultiSelect, { name: name, label: title, value: multiSelectStableValue !== null && multiSelectStableValue !== void 0 ? multiSelectStableValue : EMPTY_MULTISELECT, tooltip: infos, options: formatFieldOptions(field.options, multiSelectStableValue), onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'DATE':
                return (React.createElement(FormDate, { name: name, label: title, lang: language, tooltip: infos, value: value, time: false, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'DATETIME':
                return (React.createElement(FormDate, { name: name, label: title, tooltip: infos, value: value, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'TIME':
                return (React.createElement(FormDate, { name: name, label: title, tooltip: infos, value: value, timeOnly: true, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'YEAR':
                return (React.createElement(FormDate, { name: name, label: title, tooltip: infos, value: value, yearOnly: true, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'DATE_RANGE':
                return (React.createElement(FormDate, { name: name, label: title, tooltip: infos, value: value, time: false, selectsRange: true, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'FUNCTION_AVERAGE':
                return (React.createElement(FormInput, { type: "number", name: name, label: title, value: value, tooltip: infos, onChange: (value) => onChange && onChange({ [field.id]: value }), onBlur: () => onBlur && onBlur({ [field.id]: value }), suffix: toMoney(Number(functionValue)) +
                        ' / ' +
                        value +
                        ' = ' +
                        toMoney(Number(functionValue) / Number(value > 0 ? value : 1)), disabled: defaultValueLocked !== null && defaultValueLocked !== void 0 ? defaultValueLocked : false, size: size }));
            case 'FILE':
                return (React.createElement(FormMediaPicker, { name: name, style: "normal", label: title, infos: infos, types: "application/pdf", onMediaPickerPick: (value) => {
                        const values = [];
                        if (Array.isArray(value)) {
                            value.forEach((element) => {
                                values.push(element.id);
                            });
                        }
                        else if (value) {
                            values.push(value.id);
                        }
                        onChange && onChange({ [field.id]: values });
                    }, value: value, editable: true, restricted: true, fromPublic: fromPublic }));
            case 'HIDDEN':
                return React.createElement(FormInput, { type: "hidden", name: name, value: value });
        }
    }
    return (React.createElement(FormField, { ratio: field.type === 'TEXTAREA' ? '12' : '6' }, renderField()));
}
